import { PP_MOCK_PATIENT_IDS } from '../patients/config';
import { IV_SMILE_TYPES } from './config';
import type { IvPatientInvisalignBundle } from './types';

function bundle(
  patch: Partial<IvPatientInvisalignBundle> &
    Pick<IvPatientInvisalignBundle, 'description' | 'status' | 'statusDisplay'>,
): IvPatientInvisalignBundle {
  return {
    hasProfile: true,
    amountCoveredByInsurance: 1500,
    selectedSmileId: 'overbite',
    smileTypes: IV_SMILE_TYPES,
    concerns: [],
    communications: [],
    familyMembers: [],
    beforeImage: null,
    afterImage: null,
    video: null,
    ...patch,
  };
}

const JAMES = bundle({
  status: 'active',
  statusDisplay: 'Active',
  amountCoveredByInsurance: 2000,
  description:
    'James completed his scan on May 12 and is motivated to start before summer travel. Lead with tray check-in progress, then phase the full case with monthly payments under $200. Insurance pre-auth shows $2,000 covered — confirm Delta PPO benefit before quoting OOP.',
  selectedSmileId: 'overbite',
  concerns: [
    {
      id: 'c1',
      title: 'Timeline',
      detail: 'Wants treatment finished before August travel — needs clear tray schedule.',
    },
    {
      id: 'c2',
      title: 'Cost',
      detail: 'Asked about monthly payment options; sensitive to totals over $3,000.',
    },
    {
      id: 'c3',
      title: 'Comfort',
      detail: 'Mentioned mild gag reflex — allow breaks during scans and fittings.',
    },
  ],
  communications: [
    {
      id: 'm1',
      summary: 'Sent Invisalign financing breakdown and monthly phasing options.',
      channel: 'email',
      date: 'May 14',
      time: '10:22 AM',
    },
    {
      id: 'm2',
      summary: 'Confirmed scan appointment and pre-visit instructions.',
      channel: 'sms',
      date: 'May 10',
      time: '2:30 PM',
    },
    {
      id: 'm3',
      summary: 'Discussed insurance coverage estimate and next-step consult.',
      channel: 'call',
      date: 'May 8',
      time: '11:05 AM',
    },
  ],
  familyMembers: [{ id: 'f1', name: 'Lily Chen', relationship: 'Partner' }],
});

const AMARA = bundle({
  status: 'prospect',
  statusDisplay: 'Prospect',
  amountCoveredByInsurance: 1200,
  description:
    'Amara asked about aligners at her April hygiene visit — interested but needs gentle pacing. Anchor to the October family wedding; she shuts down if rushed. Offer nitrous for scan day and frame as a small step before whitening.',
  selectedSmileId: 'gap-teeth',
  concerns: [
    {
      id: 'c1',
      title: 'Anxiety',
      detail: 'Childhood dental trauma — never push; offer nitrous and extra time.',
    },
    {
      id: 'c2',
      title: 'Timeline',
      detail: 'Family wedding in October — wants visible progress for photos.',
    },
  ],
  communications: [
    {
      id: 'm1',
      summary: 'Hygienist noted Invisalign interest in visit summary — follow up by text.',
      channel: 'sms',
      date: 'Apr 24',
      time: '4:10 PM',
    },
    {
      id: 'm2',
      summary: 'Shared wedding-timeline whitening + aligner overview email.',
      channel: 'email',
      date: 'Apr 26',
      time: '9:15 AM',
    },
  ],
  familyMembers: [
    { id: 'f1', name: 'Jane Brooks', relationship: 'Spouse' },
    { id: 'f2', name: 'Emily', relationship: 'Daughter' },
    { id: 'f3', name: 'Michael', relationship: 'Son' },
  ],
});

const SARA = bundle({
  status: 'prospect',
  statusDisplay: 'Prospect',
  amountCoveredByInsurance: 0,
  description:
    'New patient with wedding in 8 weeks — primarily interested in whitening but open to minor alignment if timeline allows. Keep scope realistic; refer full ortho case only if she asks.',
  selectedSmileId: 'generally-straight',
  concerns: [
    {
      id: 'c1',
      title: 'Timeline',
      detail: 'Wedding in 8 weeks — any aligner plan must be honest about feasibility.',
    },
    {
      id: 'c2',
      title: 'Budget',
      detail: 'No insurance on file — lead with combo pricing, not list fees.',
    },
  ],
  communications: [
    {
      id: 'm1',
      summary: 'Discussed whitening package at new patient exam — aligners mentioned.',
      channel: 'call',
      date: 'May 18',
      time: '11:40 AM',
    },
  ],
  familyMembers: [],
});

const TOM = bundle({
  status: 'prospect',
  statusDisplay: 'Prospect',
  amountCoveredByInsurance: 800,
  description:
    'Long-term patient with recall overdue — past consult noted crowding but low urgency. Pre-med required for any scan visit. Prefer SMS outreach; face patient when speaking (hearing).',
  selectedSmileId: 'crooked-teeth',
  concerns: [
    {
      id: 'c1',
      title: 'Medical',
      detail: 'Pre-med antibiotic protocol required before any invasive visit.',
    },
    {
      id: 'c2',
      title: 'Engagement',
      detail: 'Missed recall — re-establish trust before ortho pitch.',
    },
  ],
  communications: [
    {
      id: 'm1',
      summary: 'Recall voicemail left — mentioned optional aligner consult if interested.',
      channel: 'call',
      date: 'May 20',
      time: '3:05 PM',
    },
  ],
  familyMembers: [{ id: 'f1', name: 'Maria Rivera', relationship: 'Spouse' }],
});

export const IV_BY_PATIENT: Record<string, IvPatientInvisalignBundle> = {
  'james-chen': JAMES,
  'amara-reza': AMARA,
  'sara-malik': SARA,
  'tom-rivera': TOM,
};

/** Default bundle for directory patients and unknown ids. */
export const IV_DEFAULT = AMARA;

/** Ensures every canonical mock patient id resolves to a profile. */
for (const id of PP_MOCK_PATIENT_IDS) {
  if (!IV_BY_PATIENT[id]) {
    IV_BY_PATIENT[id] = IV_DEFAULT;
  }
}
